import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, MapPin, Star } from "lucide-react";
import { BRAND, DayHireMark } from "@/lib/brand";

export default function Landing() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#F3EFFA] text-[#23272B]">
      {/* Header */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between relative z-10">
        <DayHireMark size={40} withWordmark data-testid="landing-logo" />
        <div className="flex items-center gap-1 sm:gap-2">
          <Link to="/login" data-testid="landing-login-link">
            <Button variant="ghost" className="rounded-full h-10 px-3 sm:px-4 text-sm text-[#3E2A4E] hover:bg-[#EBE4F5]">Log in</Button>
          </Link>
          <Link to="/register" data-testid="landing-signup-link">
            <Button className="bg-[#3E2A4E] hover:bg-[#2E1F3B] text-[#F3EFFA] rounded-full px-4 sm:px-5 h-10 text-sm">Sign up</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 sm:pt-16 pb-16 sm:pb-24 relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 float-in">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D9FF3D] text-[#23272B] text-xs font-bold mb-5">
              <span className="w-2 h-2 rounded-full bg-[#3E2A4E] animate-pulse" />
              Moscow–Pullman · Local help, on demand
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.03] text-[#3E2A4E]">
              Good neighbors,
              <span className="block">hired by the day.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[#23272B]/80 max-w-xl leading-relaxed">
              Post a task in 30 seconds. Get matched with vetted local helpers across Moscow, ID and Pullman, WA.
              Pay only when the job's done — with an anonymous rating you can trust.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register?role=local" data-testid="landing-cta-local">
                <Button size="lg" className="bg-[#D9FF3D] hover:bg-[#C6EB2D] text-[#23272B] rounded-full px-7 h-12 btn-primary-glow w-full sm:w-auto font-bold">
                  I need help <ArrowRight size={18} className="ml-1" />
                </Button>
              </Link>
              <Link to="/register?role=helper" data-testid="landing-cta-helper">
                <Button size="lg" variant="outline" className="rounded-full px-7 h-12 border-[#3E2A4E] text-[#3E2A4E] hover:bg-[#EBE4F5] w-full sm:w-auto font-semibold">
                  I'm a helper
                </Button>
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { icon: ShieldCheck, label: "Secure payments" },
                { icon: MapPin, label: "Moscow–Pullman" },
                { icon: Star, label: "Trusted ratings" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-sm text-[#23272B]">
                  <Icon size={18} className="text-[#3E2A4E]" />
                  <span className="font-semibold">{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative float-in stagger-2">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-[#3E2A4E]/10">
              <img
                src="https://images.unsplash.com/photo-1505798577917-a65157d3320a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Local helper at work"
                className="w-full h-[320px] sm:h-[520px] object-cover"
              />
              <div className="absolute inset-x-4 bottom-4 bg-white/95 backdrop-blur rounded-2xl p-4 shadow-lg border border-[#3E2A4E]/10">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-bold text-[#3E2A4E] uppercase tracking-wider">Yard cleanup</div>
                    <div className="font-heading font-black text-[#23272B] mt-1">Rake leaves & haul bags</div>
                    <div className="text-xs text-[#23272B]/60 mt-1 flex items-center gap-1">
                      <MapPin size={12} /> 1.2 mi away · Moscow–Pullman
                    </div>
                  </div>
                  <div className="font-mono-money text-sm px-3 py-1.5 rounded-xl bg-[#D9FF3D] text-[#23272B]">
                    $85
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20 relative z-10">
        <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-[#3E2A4E] mb-6 sm:mb-8">How it works</h2>
        <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
          {[
            { n: "01", t: "Post or browse", d: "Locals describe the task and set a budget. Helpers browse open jobs across Moscow and Pullman." },
            { n: "02", t: "Match & agree", d: "Accept an application or hire a helper directly from their profile." },
            { n: "03", t: "Pay & rate", d: "Pay through the app after the job's done. Leave a rating — anonymous if you prefer." },
          ].map((s) => (
            <div key={s.n} className="bg-white rounded-2xl border border-[#3E2A4E]/10 p-6 card-hover">
              <div className="font-mono-money text-[#3E2A4E] text-sm">{s.n}</div>
              <h3 className="font-heading font-black text-lg text-[#23272B] mt-2">{s.t}</h3>
              <p className="text-sm text-[#23272B]/70 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#3E2A4E]/10 bg-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between gap-4 flex-wrap">
          <DayHireMark size={28} withWordmark />
          <div className="text-xs text-[#23272B]/60">{BRAND.tagline} · © {new Date().getFullYear()} Day Hire</div>
        </div>
      </footer>
    </div>
  );
}
